import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'wouter';

interface FavoriteButtonProps {
  statisticId: string;
  initialFavorited?: boolean;
  initialCount?: number;
  onToggle?: (isFavorited: boolean, count: number) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
  /** When true, trust initialFavorited from parent (e.g. engagement endpoint) */
  skipStatusCheck?: boolean;
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  statisticId,
  initialFavorited = false,
  initialCount = 0,
  onToggle,
  className = '',
  size = 'md',
  showCount = true,
  skipStatusCheck = false,
}) => {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [favoritesCount, setFavoritesCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(!skipStatusCheck);

  // Update favorites count when initialCount changes (e.g., after metrics are loaded)
  useEffect(() => {
    setFavoritesCount(initialCount);
  }, [initialCount]);

  // Update favorited status when initialFavorited changes
  useEffect(() => {
    setIsFavorited(initialFavorited);
  }, [initialFavorited]);

  // Check initial favorite status when component mounts
  useEffect(() => {
    if (skipStatusCheck) {
      setIsChecking(false);
      return;
    }
    if (user) {
      checkFavoriteStatus();
    } else {
      setIsChecking(false);
    }
  }, [user, statisticId, skipStatusCheck]);

  const checkFavoriteStatus = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(`/api/favorites/check/${statisticId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.isFavorited);
      }
    } catch (error) {
      console.error('Error checking favorite status:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleToggle = async () => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`/api/favorites/${statisticId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.isFavorited);
        setFavoritesCount(data.favoritesCount);
        
        // Call callback if provided
        if (onToggle) {
          onToggle(data.isFavorited, data.favoritesCount);
        }
      } else if (response.status === 401) {
        // Handle authentication error
        navigate('/login');
      } else {
        console.error('Failed to toggle favorite');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24
  };

  if (isChecking) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className={`${sizeClasses[size]} bg-gray-200 animate-pulse rounded-full`} />
        {showCount && (
          <div className="w-8 h-4 bg-gray-200 animate-pulse rounded" />
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`
          ${sizeClasses[size]} 
          flex items-center justify-center 
          rounded-full 
          transition-all duration-200 
          hover:scale-105 
          active:scale-95
          disabled:opacity-50 
          disabled:cursor-not-allowed
          ${isFavorited 
            ? 'bg-red-500 text-white hover:bg-red-600' 
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-red-500'
          }
        `}
        title={isFavorited ? 'Quitar de favoritos' : 'Añadir a favoritos'}
      >
        <Heart 
          size={iconSizes[size]} 
          className={`transition-all duration-200 ${
            isFavorited ? 'fill-current' : ''
          }`}
        />
      </button>
      
      {showCount && favoritesCount > 0 && (
        <span className="text-sm text-gray-600 font-medium">
          {favoritesCount.toLocaleString()}
        </span>
      )}
    </div>
  );
};

export default FavoriteButton; 